import React, { useState, useEffect } from 'react';
import './Matching.css';
import { filterNomenclature, countMatches } from '../utils/searchHelpers';

function Matching({ token, orderId, matchingData, onComplete, onNavigate }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [nomenclatureList, setNomenclatureList] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (matchingData && matchingData.unmatched_items) {
      setItems(matchingData.unmatched_items);
      if (matchingData.unmatched_items.length === 0) {
        // Если нет несопоставленных элементов, сразу переходим к результатам
        onComplete();
      }
    }
    // Загрузить полный список номенклатуры для поиска
    loadNomenclature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingData]);

  const loadNomenclature = async () => {
    try {
      const response = await fetch(`${API_URL}/api/nomenclature/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setNomenclatureList(data.map(item => item.name));
      }
    } catch (err) {
      // ignore
    }
  };

  const currentItem = items[currentIndex];
  const progress = items.length > 0 ? ((currentIndex + 1) / items.length) * 100 : 100;

  const handleConfirm = async () => {
    if (!selectedMatch) {
      setError('Выберите вариант из списка или введите свой');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Получаем ID результата для текущей позиции
      const resultsResponse = await fetch(
        `${API_URL}/api/matching/results/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!resultsResponse.ok) {
        throw new Error('Не удалось загрузить результаты');
      }

      const results = await resultsResponse.json();
      const currentResult = results.find(
        (r) => r.order_name === currentItem.order_name && r.status === 'manual'
      );

      if (!currentResult) {
        throw new Error('Результат не найден');
      }

      // Подтверждаем сопоставление
      const confirmResponse = await fetch(
        `${API_URL}/api/matching/confirm/${currentResult.id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ matched_name: selectedMatch }),
        }
      );

      if (!confirmResponse.ok) {
        throw new Error('Ошибка при подтверждении сопоставления');
      }

      // Переход к следующему элементу
      if (currentIndex < items.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setSelectedMatch('');
        setSearchTerm('');
      } else {
        // Все элементы обработаны
        onComplete();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedMatch('');
      setSearchTerm('');
    } else {
      onComplete();
    }
  };

  const handleCustomInput = (value) => {
    setSelectedMatch(value);
  };

  if (!currentItem) {
    return (
      <div className="matching-container">
        <div className="loading">Загрузка данных сопоставления...</div>
      </div>
    );
  }

  return (
    <div className="matching-container">
      <div className="matching-header">
        <h1>Ручное сопоставление</h1>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <p className="progress-text">
          Позиция {currentIndex + 1} из {items.length}
        </p>
      </div>

      <div className="matching-content">
        <div className="order-item-card">
          <h2>Товар из заказа:</h2>
          <div className="order-item-info">
            <h3>{currentItem.order_name}</h3>
            <p className="quantity">Количество: {currentItem.quantity}</p>
          </div>
        </div>

        <div className="suggestions-section">
          <h2>Выберите соответствие из номенклатуры:</h2>

          {error && <div className="error-message">{error}</div>}

          {/* Поле поиска */}
          <div className="search-box">
            <label htmlFor="search-input">🔍 Поиск по ключевым словам:</label>
            <input
              id="search-input"
              type="text"
              className="search-input"
              placeholder="Например: сверло, метчик, фреза..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
            {searchTerm && (
              <small className="search-count">
                Найдено: {countMatches(nomenclatureList, searchTerm)} позиций
              </small>
            )}
          </div>

          {/* Предложения от системы или отфильтрованный список */}
          {searchTerm ? (
            // Если есть поисковый запрос, показываем отфильтрованный список
            <div className="suggestions-list">
              {filterNomenclature(nomenclatureList, searchTerm)
                .slice(0, 50)
                .map((item, index) => (
                  <label key={index} className="suggestion-item">
                    <input
                      type="radio"
                      name="suggestion"
                      value={item}
                      checked={selectedMatch === item}
                      onChange={(e) => setSelectedMatch(e.target.value)}
                      disabled={loading}
                    />
                    <span className="suggestion-text">{item}</span>
                  </label>
                ))}
              {filterNomenclature(nomenclatureList, searchTerm).length === 0 && (
                <p className="no-suggestions">
                  По вашему запросу ничего не найдено. Попробуйте другие ключевые слова.
                </p>
              )}
            </div>
          ) : (
            // Если нет поискового запроса, показываем предложения системы
            currentItem.suggestions && currentItem.suggestions.length > 0 ? (
              <div className="suggestions-list">
                {currentItem.suggestions.map((suggestion, index) => {
                  // Support both old format (strings) and new format (objects with score)
                  const suggestionName = typeof suggestion === 'string' ? suggestion : suggestion.name;
                  const suggestionScore = typeof suggestion === 'object' ? suggestion.score : null;

                  // Determine quality class based on score
                  let qualityClass = 'quality-low';
                  if (suggestionScore >= 70) qualityClass = 'quality-high';
                  else if (suggestionScore >= 50) qualityClass = 'quality-medium';

                  return (
                    <label key={index} className={`suggestion-item ${qualityClass}`}>
                      <input
                        type="radio"
                        name="suggestion"
                        value={suggestionName}
                        checked={selectedMatch === suggestionName}
                        onChange={(e) => setSelectedMatch(e.target.value)}
                        disabled={loading}
                      />
                      <span className="suggestion-content">
                        <span className="suggestion-text">{suggestionName}</span>
                        {suggestionScore !== null && (
                          <span className="suggestion-score">
                            {suggestionScore >= 70 && '🟢 '}
                            {suggestionScore >= 50 && suggestionScore < 70 && '🟡 '}
                            {suggestionScore < 50 && '🔴 '}
                            {suggestionScore}%
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="no-suggestions">
                Нет автоматических предложений. Используйте поиск выше или введите название вручную ниже.
              </p>
            )
          )}

          <div className="custom-input-section">
            <h3>Или введите свой вариант:</h3>
            <input
              type="text"
              className="custom-input"
              placeholder="Введите точное название из номенклатуры"
              value={selectedMatch}
              onChange={(e) => handleCustomInput(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="matching-actions">
          <button
            className="skip-btn"
            onClick={handleSkip}
            disabled={loading}
          >
            Пропустить
          </button>
          <button
            className="confirm-btn"
            onClick={handleConfirm}
            disabled={loading || !selectedMatch}
          >
            {loading ? 'Сохранение...' : 'Подтвердить'}
          </button>
        </div>

        <div className="info-box">
          <p>
            💡 Выберите наиболее подходящий вариант из списка или введите свой.
            Система запомнит ваш выбор для будущих заказов.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Matching;
